/** OnboardStudioType — the pre-profile picker that sits BEFORE the
 *  main studio onboarding form.
 *
 *  Per user's spec (Feb 2026):
 *    · A studio must pick BOTH (a) what kind of facility they run AND
 *      (b) which trade associations they're affiliated with BEFORE
 *      entering any legal name / address / owner details.
 *    · Mirrors the /onboard/agency-type pattern used by L2.
 *
 *  Selection flows to `/onboard/studio-details` via route state.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import OnboardBackButton from "@/components/OnboardBackButton";
import AffiliationsPicker from "@/components/AffiliationsPicker";
import { ArrowRight, Check, Building2, Award } from "lucide-react";
import { getBackendUrl } from "../lib/backend-url";

const API = `${getBackendUrl()}/api`;

export default function OnboardStudioType() {
  const nav = useNavigate();
  const [meta, setMeta] = useState(null);
  const [picked, setPicked] = useState(new Set());
  const [sectionAffiliations, setSectionAffiliations] = useState([]);

  useEffect(() => {
    axios.get(`${API}/meta/studio-types`).then(({ data }) => setMeta(data));
  }, []);

  const groups = useMemo(() => {
    if (!meta?.types) return [];
    const buckets = new Map();
    for (const t of meta.types) {
      const cat = t.category || "other";
      if (!buckets.has(cat)) {
        buckets.set(cat, { key: cat, label: t.category_label || cat, items: [] });
      }
      buckets.get(cat).items.push(t);
    }
    return Array.from(buckets.values());
  }, [meta]);

  const proKeys = useMemo(() => new Set(meta?.pro_facility_keys || []), [meta]);
  const requiresAffiliation = useMemo(() => {
    for (const k of picked) if (proKeys.has(k)) return true;
    return false;
  }, [picked, proKeys]);

  const toggle = (key) => {
    setPicked((p) => {
      const n = new Set(p);
      if (n.has(key)) n.delete(key); else n.add(key);
      return n;
    });
  };

  const canProceed =
    picked.size > 0 &&
    (!requiresAffiliation || sectionAffiliations.length > 0);

  const proceed = () => {
    if (!canProceed) return;
    nav("/onboard/studio-details", {
      state: {
        studioFacilityTypes: Array.from(picked),
        sectionAffiliations,
      },
    });
  };

  return (
    <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-14"
         data-testid="onboard-studio-type-page">
      <OnboardBackButton fallback="/onboard" testId="onboard-studio-type-back" />

      <div className="mt-6">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">
          L4 · Sign up · Step 1 of 2
        </div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">
          What kind of studio are you?
        </h1>
        <p className="mt-3 text-sm text-white/60 max-w-2xl leading-relaxed">
          Pick every facility you offer AND the trade associations you&apos;re
          affiliated with. This decides your office floor layout, your Support
          Systems, and which producer searches surface you. You can add more
          later from Settings.
        </p>
      </div>

      {meta === null ? (
        <div className="mt-10 text-white/50 text-sm">Loading…</div>
      ) : (
        <>
          {/* ─── Section A · Facility types ─── */}
          <div className="mt-10" data-testid="studio-type-section-facilities">
            <div className="flex items-start gap-3 mb-4 p-3 rounded-lg bg-amber-500/8 border border-amber-500/30">
              <Building2 size={16} className="text-amber-300 shrink-0 mt-0.5" />
              <div className="text-xs text-white/75 leading-relaxed">
                <span className="text-amber-200 font-semibold">Facility Type *</span>{" "}
                — one studio can offer multiple facilities (a shooting floor + a
                mixing suite is common). Pro-grade facilities (mixing · mastering
                · foley · VFX) require an accreditation below.
              </div>
            </div>
            {groups.map((g) => (
              <div key={g.key} className="mt-6"
                   data-testid={`studio-type-group-${g.key}`}>
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/45 mb-2">
                  {g.label}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {g.items.map((t) => {
                    const on = picked.has(t.key);
                    const isPro = proKeys.has(t.key);
                    return (
                      <button key={t.key} onClick={() => toggle(t.key)}
                        type="button"
                        className={
                          "text-left rounded-2xl border p-5 transition-all relative " +
                          (on
                            ? "border-white bg-white/[0.06] shadow-[0_0_0_1px_rgba(255,255,255,0.35)]"
                            : "border-white/12 bg-black/25 hover:border-white/30 hover:bg-white/[0.03]")
                        }
                        data-testid={`studio-type-card-${t.key}`}>
                        {on && (
                          <div className="absolute top-4 right-4 w-5 h-5 rounded-full bg-[#F5B82E] text-black flex items-center justify-center"
                               data-testid={`studio-type-check-${t.key}`}>
                            <Check size={12} strokeWidth={3}/>
                          </div>
                        )}
                        <div className="font-display text-base uppercase tracking-tight">
                          {t.label}
                          {isPro && (
                            <span className="ml-2 align-middle text-[9px] tracking-[0.18em] uppercase text-amber-300"
                                  data-testid={`studio-type-pro-${t.key}`}>
                              Pro-Grade
                            </span>
                          )}
                        </div>
                        <p className="mt-1.5 text-[11px] text-white/55 leading-relaxed">
                          {t.blurb}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          {/* ─── Section B · Trade Association affiliations ─── */}
          <div className="mt-12" data-testid="studio-type-section-affiliations">
            <div className={
              "flex items-start gap-3 mb-4 p-3 rounded-lg " +
              (requiresAffiliation
                ? "bg-red-500/8 border border-red-500/30"
                : "bg-indigo-500/8 border border-indigo-500/30")
            }>
              <Award size={16}
                className={requiresAffiliation ? "text-red-300 shrink-0 mt-0.5" : "text-indigo-300 shrink-0 mt-0.5"} />
              <div className="text-xs text-white/75 leading-relaxed">
                {requiresAffiliation ? (
                  <>
                    <span className="text-red-200 font-semibold">Trade Association *</span>{" "}
                    Required — you picked a Pro-Grade facility. Producers only see
                    accredited houses in the &quot;Vetted&quot; ranking. Tick at
                    least one body (Dolby · MPSE · AES · SMPTE · FSPI · etc.).
                  </>
                ) : (
                  <>
                    <span className="text-indigo-200 font-semibold">Trade Association</span>{" "}
                    (optional but recommended) — accredited houses rank higher in
                    producer search. Studios not affiliated with any body may
                    tick <em>Not Registered</em>.
                  </>
                )}
              </div>
            </div>
            <AffiliationsPicker
              section="studio"
              value={sectionAffiliations}
              onChange={setSectionAffiliations}
              testidPrefix="onboard-studio-type-aff"
            />
          </div>

          {/* ─── Continue ─── */}
          <div className="mt-12 flex items-center gap-4 flex-wrap">
            <div className="text-xs text-white/55">
              {picked.size === 0
                ? "Pick at least one facility to continue."
                : `${picked.size} facility${picked.size === 1 ? "" : "s"} · ${sectionAffiliations.length} affiliation${sectionAffiliations.length === 1 ? "" : "s"}`}
            </div>
            <button
              onClick={proceed}
              disabled={!canProceed}
              type="button"
              className="ml-auto btn-themed h-11 px-6 rounded-full uppercase tracking-[0.22em] text-[11px] font-semibold inline-flex items-center gap-2 disabled:opacity-40"
              data-testid="studio-type-continue">
              Continue to Profile <ArrowRight size={13}/>
            </button>
          </div>
        </>
      )}
    </div>
  );
}
